import { useContext, useEffect } from 'react';

import { useParams } from 'js/lib/useRouter';

import { HeartbeatContext } from 'bundles/grading-common/components/heartbeat/HeartbeatContext';
import type { HeartbeatData } from 'bundles/grading-common/components/heartbeat/HeartbeatContext';

/**
 * Send heartbeat events to Retracked when the user interacts with the page.
 * On `scroll`, `mousemove`, and `keydown` events it will send the event to Retracked. The callback is throttled,
 * i.e. events will be sent only once a second.
 *
 * You can pass the data to be sent with the event using the HeartbeatData type. For example:
 * ```
 * useHeartbeat({
 *   page: 'sga_submission_grading',
 *   routeNames: [ RouteNames.ASSIGNMENT_QUICK_GRADER ],
 *   eventData: {
 *     courseId: 'some-course-id'
 *   }
 * });
 * ```
 *
 * You can also add data to the heartbeat events using function returned by the hook:
 * ```
 * const setHeartbeatData = useHeartbeat();
 * useEffect(() => {
 *   // ...
 *   setHeartbeatData({ something: 'else' });
 * });
 * ```
 *
 * `HeartbeatProvider` must wrap your page/product that you'd like to track heartbeats on. If you have multiple components
 * with `useHeartbeat`, their data will be merged based on identical routes/page/version, and sent together in the same
 * event if the route matches up with the current route, within each HeartbeatProvider wrapped session. Multiple routes are
 * allowed for a single heartbeat. An example is Legacy Grader being accessible from both Grading and Learner/Rosters tab
 * of the Educator Admin portal
 *
 * In this case, if you pass objects with the same keys, they will be overwritten, and there is no guarantee of component's
 * priority. In the event there's multiple heartbeats that need to be tracked separately within the same route, you can use
 * separate HeartbeatProvider wrappers to track them individually. You may also nest multiple HeartbeatProviders and they
 * will track internal heartbeats on their own.
 *
 * Components can have different versions to differentiate different experiences for the same page/product.
 * Default version is v1 when not provided. Example of a use case is SGA Grading where Legacy (old) Grader is v1 and Quick
 * (new) Grader is v2. In the event different versions require the same route and both need to be tracked at the same time,
 * use separate HeartbeatProvider wrappers for each version.
 *
 * In order to add new pages to track, you must add a new HeartbeatApp/HeartbeatPage/HeartbeatContextData in HeartbeatContext
 *
 * If working with a class component, use the UseHeartbeatForClassComponents component instead of this hook
 */

export const useHeartbeat = (heartbeatData: HeartbeatData) => {
  const { setHeartbeatData, addConsumer, removeConsumer } = useContext(HeartbeatContext);
  const routeParams = useParams();
  useEffect(() => {
    setHeartbeatData(heartbeatData);
    addConsumer();
    return () => {
      removeConsumer();
    };
    // force trigger a "reset" in throttle callback when route params or heartbeatData change to ensure latest set of eventData
    // are tracked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeParams, heartbeatData]);

  return setHeartbeatData;
};

export default useHeartbeat;
